import { Link, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
} from "reactstrap";
import { NotificationContainer, NotificationManager } from "react-notifications";

import useAuth from "hooks/useAuth";
import getDecodedToken from "services/getDecodedToken";
import axios from "api/axios";
import Avatar from "components/ui/Avatar";

const IconBell = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);
const IconSearch = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
);
const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const IconSettings = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const IconLogout = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const AdminNavbar = ({ brandText, profilePath = "/admin/Profile" }) => {
  const { setAuth } = useAuth() || {};
  const navigate = useNavigate();

  const decoded = getDecodedToken() || {};
  const email = decoded.sub || "";
  const username = decoded.username || (email ? email.split("@")[0] : "User");

  const handleLogout = async () => {
    try {
      await axios.post("/auth/logout", {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout API call failed", err);
    }
    localStorage.removeItem("accessToken");
    if (setAuth) setAuth({});
    try { NotificationManager.success("Logged out successfully."); } catch (_) {}
    navigate("/auth/login");
  };

  return (
    <>
      <header className="pm-topbar" role="banner">
        <div className="pm-topbar__left">
          <span
            aria-hidden="true"
            style={{
              width: 8, height: 8, borderRadius: 999,
              background: "var(--brand-primary)",
              boxShadow: "0 0 12px var(--brand-primary)",
              flexShrink: 0,
            }}
          />
          <Link
            to="/"
            className="pm-topbar__crumb"
            style={{ textDecoration: "none", color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {brandText || "Dashboard"}
          </Link>
        </div>

        <div className="pm-topbar__right">
          <button type="button" className="pm-topbar__icon-btn" aria-label="Search">
            <IconSearch />
          </button>
          <button type="button" className="pm-topbar__icon-btn" aria-label="Notifications">
            <IconBell />
            <span className="pm-dot" aria-hidden="true" />
          </button>

          <span className="pm-topbar__divider" aria-hidden="true" />

          <UncontrolledDropdown nav inNavbar>
            <DropdownToggle
              tag="button"
              className="pm-topbar__user"
              caret={false}
              style={{ background: "transparent", border: 0 }}
            >
              <Avatar name={username} size="sm" />
              <span className="pm-topbar__user-meta d-none d-md-inline-block">
                <div className="pm-topbar__user-name">{username}</div>
                <div className="pm-topbar__user-email">{email}</div>
              </span>
            </DropdownToggle>
            <DropdownMenu right className="pm-dropdown">
              <div className="pm-dropdown__header">
                <div className="pm-dropdown__header-name">{username}</div>
                <div className="pm-dropdown__header-email">{email}</div>
              </div>
              <DropdownItem
                tag={Link}
                to={profilePath}
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <IconUser /> My Profile
              </DropdownItem>
              <DropdownItem
                tag={Link}
                to={profilePath}
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <IconSettings /> Settings
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem
                onClick={handleLogout}
                className="text-danger"
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <IconLogout /> Logout
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </header>
      <NotificationContainer />
    </>
  );
};

export default AdminNavbar;
